import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { FaChevronDown } from "react-icons/fa";

function getLeadStatusTone(status) {
  const value = String(status || "").trim().toLowerCase();
  if (!value) return "neutral";
  // Order matters: "not interested" before "interested", etc.
  if (
    /(not interested|lost|rejected|cancelled|canceled|closed lost|inactive|dead)/.test(value)
  ) {
    return "danger";
  }
  if (/(won|qualified|converted|completed|active|interested|closed won)/.test(value)) {
    return "success";
  }
  if (
    /(no answer|not reachable|follow|pending|progress|warm|waiting|callback)/.test(value)
  ) {
    return "warning";
  }
  if (/(new|open|cold|contacted)/.test(value)) return "info";
  return "neutral";
}

/**
 * Colored pill status control with a custom menu (avoids native OS select styling).
 */
function PortalStatusSelect({
  value,
  label,
  options = [],
  disabled = false,
  onChange,
  ariaLabel = "Update status",
}) {
  const [open, setOpen] = useState(false);
  const [menuStyle, setMenuStyle] = useState(null);
  const rootRef = useRef(null);
  const menuRef = useRef(null);
  const currentId = value != null && value !== "" ? String(value) : "";
  const currentLabel =
    label ||
    options.find((o) => String(o.Status_Id) === currentId)?.Status ||
    "Select status";
  const tone = getLeadStatusTone(currentLabel);

  useLayoutEffect(() => {
    if (!open || !rootRef.current) return undefined;

    const place = () => {
      const rect = rootRef.current.getBoundingClientRect();
      const menuWidth = 220;
      const gap = 6;
      const spaceBelow = window.innerHeight - rect.bottom - 12;
      const openUp = spaceBelow < 200 && rect.top > spaceBelow;
      const left = Math.min(
        Math.max(8, rect.left),
        window.innerWidth - menuWidth - 8
      );
      setMenuStyle({
        position: "fixed",
        left,
        width: menuWidth,
        top: openUp ? undefined : rect.bottom + gap,
        bottom: openUp ? window.innerHeight - rect.top + gap : undefined,
        maxHeight: Math.min(280, openUp ? rect.top - 16 : spaceBelow),
      });
    };

    place();
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => {
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onDocMouseDown = (e) => {
      const inTrigger = rootRef.current && rootRef.current.contains(e.target);
      const inMenu = menuRef.current && menuRef.current.contains(e.target);
      if (!inTrigger && !inMenu) setOpen(false);
    };
    const onKeyDown = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDocMouseDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onDocMouseDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  const pick = (statusId) => {
    setOpen(false);
    if (String(statusId) === currentId) return;
    onChange?.(statusId);
  };

  return (
    <div className={`cportal-status-dd${open ? " is-open" : ""}`} ref={rootRef}>
      <button
        type="button"
        className={`cportal-status-dd-trigger cportal-status-dd-trigger--${tone}`}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel}
        title="Update status"
        onClick={(e) => {
          e.stopPropagation();
          if (!disabled) setOpen((v) => !v);
        }}
      >
        <span className="cportal-status-dd-label">{currentLabel}</span>
        <FaChevronDown className="cportal-status-dd-caret" aria-hidden />
      </button>

      {open &&
        menuStyle &&
        createPortal(
          <ul
            ref={menuRef}
            className="cportal-status-dd-menu"
            role="listbox"
            aria-label={ariaLabel}
            style={menuStyle}
          >
            {options.map((s) => {
              const id = String(s.Status_Id);
              const selected = id === currentId;
              const optionTone = getLeadStatusTone(s.Status);
              return (
                <li key={s.Status_Id} role="presentation">
                  <button
                    type="button"
                    role="option"
                    aria-selected={selected}
                    className={`cportal-status-dd-option cportal-status-dd-option--${optionTone}${
                      selected ? " is-selected" : ""
                    }`}
                    onClick={(e) => {
                      e.stopPropagation();
                      pick(s.Status_Id);
                    }}
                  >
                    <span className={`cportal-status-dot cportal-status-dot--${optionTone}`} aria-hidden />
                    {s.Status}
                  </button>
                </li>
              );
            })}
          </ul>,
          document.body
        )}
    </div>
  );
}

export default PortalStatusSelect;
export { getLeadStatusTone };
